import React, { PureComponent } from 'react';
import { Elements } from 'react-stripe-elements';
import get from 'lodash/get';
import { message } from 'antd';

import Currency from '$app/components/vendor/Currency';
import { FeedbackMutedIcon } from './styled';
import { HeaderBlockContainer } from '../styled';
import PromoEditModal from './PromoEditModal';
import CustomerEditModal from './CustomerEditModal';
import EditVrmModal from './EditVrmModal';
import MuteFeedbackModal from './MuteFeedbackModal';
import UpdatePaymentModal from './UpdatePaymentModal';
import ConfirmationMailModal from './ConfirmationMailModal';
import GenerateInvoiceModal from './GenerateInvoiceModal';
import TagsModal from './TagsModal';

import { FLEET_BOOKING_SOURCES, UTM_CAMPAIGNS, UTM_MEDIUMS, UTM_SOURCES } from '../../../ListBookings/constants';

import Title from '../../../../components/Title';
import P from '../../../../components/P';
import PaymentMethodTag from './PaymentMethodTag';
import BookingDropdown from './BookingDropdown';
import {
  buildCreateBookingUrl,
  fetchCustomerServicePromo,
  openFixterAccessLink,
} from './api';

const getErrorMessage = (error) => get(error, 'response.data.message', error.message);

class CustomerDetails extends PureComponent {
  state = {
    openedPopup: null,
    customerServicePromo: null,
  };

  componentDidMount() {
    const { customer } = this.props;
    if (customer?.id && customer?.firstName) {
      this.loadCustomerServicePromo(customer.id, customer.firstName);
    }
  }

  componentDidUpdate(prevProps) {
    const { customer } = this.props;
    if (
      customer?.id &&
      customer?.firstName &&
      (prevProps.customer?.id !== customer.id || prevProps.customer?.firstName !== customer.firstName)
    ) {
      this.loadCustomerServicePromo(customer.id, customer.firstName);
    }
  }

  hasMutedFeedbackNote = (notes = []) => notes.find((note) => note.noteType === 'no-trustpilot');

  openPopup = (openedPopup) => {
    this.setState({ openedPopup });

    if (openedPopup === 'PromoEditModal') {
      const { customer } = this.props;
      if (customer?.id && customer?.firstName) {
        this.loadCustomerServicePromo(customer.id, customer.firstName);
      }
    }
  };

  closePopup = () => {
    this.setState({ openedPopup: null });
  };

  loadCustomerServicePromo = async (customerId, customerFirstName) => {
    try {
      const { data } = await fetchCustomerServicePromo(customerId, customerFirstName);
      this.setState({ customerServicePromo: data });
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  };

  openFixter = async ({ userId, bookingId }) => {
    message.info('Requesting access url..');

    try {
      const { data } = await openFixterAccessLink(userId, bookingId);
      message.success('Success');
      window.open(data.accessLink, '_blank');
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  };

  createBooking = async ({ userId, bookingId, utmMedium, utmSource, utmCampaign }) => {
    if (!UTM_SOURCES.includes(utmSource)) {
      message.error(`Invalid utm_source: ${utmSource}`);
      return;
    }

    if (!UTM_MEDIUMS.includes(utmMedium)) {
      message.error(`Invalid utm_medium: ${utmMedium}`);
      return;
    }

    if (!UTM_CAMPAIGNS.includes(utmCampaign)) {
      message.error(`Invalid utm_campaign: ${utmCampaign}`);
      return;
    }

    message.info('Requesting access url..');

    try {
      const { data } = await openFixterAccessLink(userId, bookingId);
      const accessLink = buildCreateBookingUrl(data.accessLink, userId, utmMedium, utmSource, utmCampaign);
      message.success('Success');
      window.open(accessLink, '_blank');
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  };

  render() {
    const { openedPopup, customerServicePromo } = this.state;
    const { booking, customer, notes, authUser } = this.props;
    const {
      paymentMethod: { paymentMethod },
    } = booking;

    const overlaysMenu = [
      { key: 'edit_customer_details', text: 'Edit Customer Details', popup: 'CustomerEditModal' },
      { key: 'issue_promo_code', text: 'Issue Promo Code', popup: 'PromoEditModal' },
      { key: 'edit_vrm', text: 'Edit VRM', popup: 'EditVrmModal' },
      { key: 'tag_booking', text: 'Tag Booking', popup: 'TagsModal' },
    ];

    if (booking.paymentMethod.status !== 'paid' && booking.masterStatusId !== 11000) {
      overlaysMenu.push({
        key: 'edit_payment_details',
        text: 'Edit Payment Details',
        popup: 'EditPaymentDetailsModal',
      });
    }

    const feedbackMuted = this.hasMutedFeedbackNote(notes);

    if (!feedbackMuted) {
      overlaysMenu.push({ key: 'mute_feedback', text: 'Mute Feedback', popup: 'MuteFeedbackModal' });
    }

    if (![11000, 20000].includes(booking.masterStatusId)) {
      overlaysMenu.push({
        key: 'send_confirmation_mail',
        text: 'Send Confirmation Mail',
        popup: 'ConfirmationMailModal',
      });
    }

    overlaysMenu.push({
      key: 'view_account_no_booking',
      text: 'View account - no booking',
      popup: 'OpenFixter',
    });

    if ( booking.masterStatusId >= 10700 && booking.masterStatusId !== 20000) {
      overlaysMenu.push({
        key: 'generate_customer_invoice',
        text: 'Generate customer invoice',
        popup: 'GenerateInvoiceModal',
      });
    }

    const commonFleetProps = {
      popup: 'CreateBooking',
      utm_medium: 'fleets',
      utm_campaign: 'fleet_booking',
    };

    overlaysMenu.push(
      {
        key: 'create_phone_sales_booking',
        text: 'Create Phone sales booking',
        popup: 'CreateBooking',
        utm_medium: 'sales',
        utm_source: 'call',
        utm_campaign: 'repeat_booking',
      },
      {
        key: 'create_warranty_booking',
        text: 'Create Warranty booking',
        popup: 'CreateBooking',
        utm_medium: 'cs',
        utm_source: 'call',
        utm_campaign: 'warranty_booking',
      },
      {
        key: 'create_fleet_booking',
        text: 'Create Fleet booking',
        popup: null,
        subMenuItems: FLEET_BOOKING_SOURCES.map(({ key, text, utmSource }) => ({
          key,
          text,
          utm_source: utmSource,
          ...commonFleetProps,
        })),
      }
    );

    return (
      <HeaderBlockContainer>
        <span>
          <Title className="title_customer-name">
            {customer.firstName} {customer.lastName}
            {feedbackMuted ? <FeedbackMutedIcon src="/assets/images/bubble-chat-block-2.svg" /> : null}
            {customerServicePromo &&
            customerServicePromo.status !== 'PENDING' &&
            customerServicePromo.discountAmountPence ? (
              <span className="tag promo">
                <Currency>{customerServicePromo.discountAmountPence}</Currency> promo
              </span>
            ) : null}
            {booking.partner ? <span className="tag partner">{booking.partner}</span> : null}
          </Title>
          <P>{customer.email}</P>
          <P>{customer.mobileNumber}</P>
          <P className="payment-method">
            <PaymentMethodTag>{paymentMethod.source}</PaymentMethodTag>
          </P>
        </span>
        <BookingDropdown
          authUser={authUser}
          booking={booking}
          createBooking={this.createBooking}
          openFixter={this.openFixter}
          openPopup={this.openPopup}
          overlaysMenu={overlaysMenu}
        />
        <EditVrmModal
          booking={booking}
          closePopup={this.closePopup}
          openedPopup={openedPopup}
        />
        <TagsModal booking={booking} closePopup={this.closePopup} openedPopup={openedPopup} />
        <CustomerEditModal
          closePopup={this.closePopup}
          customer={customer}
          openedPopup={openedPopup}
          segment={booking.segment}
        />
        <PromoEditModal
          closePopup={this.closePopup}
          customer={customer}
          customerServicePromo={customerServicePromo}
          openedPopup={openedPopup}
          segment={booking.segment}
        />
        <MuteFeedbackModal
          authUser={authUser}
          booking={booking}
          closePopup={this.closePopup}
          customer={customer}
          openedPopup={openedPopup}
        />
        <ConfirmationMailModal
          booking={booking}
          closePopup={this.closePopup}
          openedPopup={openedPopup}
        />
        <GenerateInvoiceModal
          authUser={authUser}
          booking={booking}
          closePopup={this.closePopup}
          openedPopup={openedPopup}
        />
        <Elements>
          <UpdatePaymentModal
            booking={booking}
            card={{
              ...get(booking, 'paymentMethod.paymentMethod.display', {}),
              source: get(booking, 'paymentMethod.paymentMethod.source'),
            }}
            closePopup={this.closePopup}
            openedPopup={openedPopup}
          />
        </Elements>
      </HeaderBlockContainer>
    );
  }
}

export default CustomerDetails;
